import { commonTestIds } from '../../../common/utils/commonTestIds';
import { OrderDocumentOptions, ViewInLibraryStatus } from '../typing/enums';
import { IOrderViewerMainStore } from '../typing/stores';
import { isOrderDrm } from '../utils/orderUtils';
import LinkoutMessage from './linkoutMessage';
import Message from './message';
import { createStyles, makeStyles } from '@mui/styles';
import { RupLoader, RupNotification } from 'copyright-clearance-center-common-ui-react';
import { inject, observer } from 'mobx-react';
import { useEffect, useState } from 'react';

export interface IDocumentIframeProps {
	deliveryLink?: string;
	message?: string;
	options?: OrderDocumentOptions;
	viewInLibraryStatus?: ViewInLibraryStatus;
	willBeDownloaded?: boolean;
	setIframeLoading?: (loading: boolean) => void;
}

const useStyles = makeStyles(() =>
	createStyles({
		documentIframe: {
			width: '100%',
			height: '100%'
		}
	})
);

const DocumentIframe: React.FunctionComponent<IDocumentIframeProps> = props => {
	const [loading, setLoading] = useState(true);
	const classes = useStyles();
	const iframeLoading = (isloading: boolean): void => {
		setLoading(isloading);
		props.setIframeLoading(isloading);
	};

	useEffect(() => {
		if (!props.willBeDownloaded) {
			iframeLoading(true);
		} else {
			iframeLoading(false);
		}
	}, []);

	const isDrm: boolean = isOrderDrm(props.options);
	const isLinkout: boolean = (props.options & OrderDocumentOptions.LinkOut) === OrderDocumentOptions.LinkOut;

	let toolTipMessage;
	if (isDrm) {
		toolTipMessage = localize(
			'This document is protected by Digital Rights Management and cannot be displayed inline. Click to open the document in a new browser tab.'
		);
	} else if (isLinkout) {
		toolTipMessage = localize(
			'This document is located at an external site. Click to open the external site in a new browser tab.'
		);
	} else if (props.willBeDownloaded) {
		toolTipMessage = localize(
			'This document cannot be displayed inline. Please check your configured delivery method (usually email) to access this document. Alternatively, click to download the document.'
		);
	} else {
		toolTipMessage = localize(
			'Select “View in Library” to open your document in your personal library, where additional features are available.'
		);
	}

	return (
		<>
			{props.viewInLibraryStatus === ViewInLibraryStatus.Enabled &&
				!isDrm &&
				!isLinkout &&
				!props.willBeDownloaded && <RupNotification text={toolTipMessage} icon={true} />}
			{(isDrm || isLinkout) && (
				<RupNotification
					text={toolTipMessage}
					icon={true}
					primaryButtonText={localize('Open')}
					onPrimaryButtonClick={() => window.open(props.message, '_blank')}
				/>
			)}
			{props.willBeDownloaded && (
				<RupNotification
					text={toolTipMessage}
					icon={true}
					primaryButtonText={localize('Download')}
					onPrimaryButtonClick={() => window.open(props.deliveryLink, '_blank')}
				/>
			)}
			{isLinkout ? <LinkoutMessage /> : props.message && <Message message={props.message} />}
			{!isDrm && props.deliveryLink && loading && (
				<RupLoader style={{ margin: 'auto' }} label="Loading document..." />
			)}
			{props.deliveryLink && !props.willBeDownloaded && (
				<iframe
					hidden={loading}
					id="ViewerFrame"
					onLoad={() => iframeLoading(false)}
					className={classes.documentIframe}
					data-test-id={commonTestIds.documentFrame}
					src={props.deliveryLink}
				/>
			)}
		</>
	);
};

export default inject((stores: IOrderViewerMainStore) => ({
	deliveryLink: stores.orderViewerStore.viewModel.documentModel.deliveryLink,
	message: stores.orderViewerStore.viewModel.documentModel.message,
	options: stores.orderViewerStore.viewModel.documentModel.documentOptions,
	willBeDownloaded: stores.orderViewerStore.viewModel.documentModel.willBeDownloaded,
	viewInLibraryStatus: stores.orderViewerStore.viewInLibraryStatus,
	setIframeLoading: stores.orderViewerStore.setIframeLoading
}))(observer(DocumentIframe));
